import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import { ApplicationState } from '@/store';
import { mdiNull, mdiZipBoxOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React from 'react';
import { useSelector } from 'react-redux';
import _ from 'underscore';
import { TypeAccepted } from '..';

interface Props {
    typeAccepted: TypeAccepted;
    fileUploadData: FileUploadData;
    fileOnQueueToConvert?: boolean;
    removeFileLinkText?: string;
    disabled?: boolean;
    isLogo?: boolean;
    isBanner?: boolean;
    removeHandler: () => void;
    onLoadedMetadata?: (minutes: number) => void;
}

interface ImageUploadPreviewProps {
    source: string;
    isLogo?: boolean;
    isBanner?: boolean;
}

interface AudioVideoUploadPreviewProps {
    mode: 'AUDIO' | 'VIDEO';
    source: string;
    onLoadedMetadata?: (minutes: number) => void;
}

const PreviewUpload: React.FC<Props> = (props: Props) => {
    const {
        typeAccepted,
        fileOnQueueToConvert,
        fileUploadData,
        removeFileLinkText,
        disabled,
        isLogo,
        isBanner,
        onLoadedMetadata,
        removeHandler,
    } = props;

    const { strings } = useSelector((state: ApplicationState) => ({ strings: state.locale.data }));
    const { preview, local, fileName } = fileUploadData;

    const isDefaultPreview = _.contains(['AUDIO', 'IMAGE', 'VIDEO', 'SUBTITLE', 'DOCUMENT'] as TypeAccepted[], typeAccepted);

    return (
        <>
            {isDefaultPreview && (
                <div className="d-flex flex-column upload-image-container">
                    {typeAccepted === 'IMAGE' && <ImageUploadPreview isBanner={isBanner} isLogo={isLogo} source={local || preview} />}

                    {(typeAccepted === 'AUDIO' || typeAccepted === 'VIDEO') && (
                        <AudioVideoUploadPreview mode={typeAccepted} onLoadedMetadata={onLoadedMetadata} source={local || preview} />
                    )}

                    <span className="file-name">{!local && fileOnQueueToConvert ? strings.THE_FILE_IS_STILL_BEING_PROCESSED : fileName}</span>

                    <div className="d-flex delete-label m-0 mt-1">
                        <i className="material-icons-outlined align-middle" onClick={() => mdiNull} role="button" tabIndex={0}>
                            {Constants.icons.DELETE}
                        </i>
                        <span onClick={() => removeHandler()} role="button" tabIndex={0}>
                            {strings.REMOVE}
                        </span>
                    </div>
                </div>
            )}

            {!isDefaultPreview && (
                <div className="d-flex flex-column upload-image-container">
                    <div className="zip-container d-flex justify-content-between align-items-center">
                        <span>
                            <Icon className="icon" horizontal path={mdiZipBoxOutline} size={1} />
                            {preview.substr(preview.indexOf('-') + 1)}
                        </span>
                        <button className="btn btn-link" disabled={disabled} onClick={() => removeHandler()}>
                            {removeFileLinkText}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

const ImageUploadPreview: React.FC<ImageUploadPreviewProps> = ({ source, isLogo, isBanner }: ImageUploadPreviewProps) => (
    <img alt="" className={`rounded ${isLogo ? 'logo-img' : isBanner ? 'banner-img' : 'default-img'}`} src={source} />
);

const AudioVideoUploadPreview: React.FC<AudioVideoUploadPreviewProps> = ({ mode, source, onLoadedMetadata }: AudioVideoUploadPreviewProps) => {
    const playerClassName = mode === 'AUDIO' ? 'audio' : 'video';

    const loadMetadataHandler = (event: React.SyntheticEvent<HTMLVideoElement>) => {
        if (onLoadedMetadata) {
            const player: HTMLVideoElement = event.target as HTMLVideoElement;
            onLoadedMetadata(Math.round(player.duration / 60));
        }
    };

    return (
        <div className="d-flex" style={{ justifyContent: 'center' }}>
            <video className={playerClassName} controls id="player" onLoadedMetadata={loadMetadataHandler} width="82%">
                <source src={source} />
            </video>
        </div>
    );
};

export default PreviewUpload;
